import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import styles from './physicalCards.module.scss';
import { actions } from '../../state';
import { Button, Input, Select } from '../../components';
import {
  buildDiscountPayload,
  buildMembershipOptions,
  EMPTY_DISCOUNT_FORM,
  MEMBERSHIPS_FETCH_PARAMS,
} from './physicalCards.helpers';
import {
  DISCOUNT_RANGE_ERROR,
  hasValidationErrors,
  validateDiscountAssignmentForm,
} from './physicalCards.validation';

const AddDiscount = ({
  dispatch,
  cardId,
  discountError,
  loading,
  memberships: { memberships },
}) => {
  const [values, setValues] = useState({ ...EMPTY_DISCOUNT_FORM });
  const [errors, setErrors] = useState({});

  const fetchMemberships = useCallback(() => {
    dispatch(
      actions.memberships.getMembershipsAction(MEMBERSHIPS_FETCH_PARAMS)
    );
  }, [dispatch]);

  const clearMemberships = useCallback(() => {
    dispatch(actions.memberships.clearMembershipsAction());
  }, [dispatch]);

  useEffect(() => {
    fetchMemberships();
    return () => clearMemberships();
  }, [fetchMemberships, clearMemberships]);

  const membershipOptions = useMemo(() => buildMembershipOptions(memberships), [
    memberships,
  ]);

  const liveErrors = useMemo(() => validateDiscountAssignmentForm(values), [
    values,
  ]);

  const displayErrors = useMemo(() => {
    const next = { ...errors };

    if (liveErrors.discount_valid_to) {
      next.discount_valid_to = liveErrors.discount_valid_to;
    } else if (next.discount_valid_to === DISCOUNT_RANGE_ERROR) {
      delete next.discount_valid_to;
    }

    return next;
  }, [errors, liveErrors]);

  const isSubmitDisabled =
    loading || hasValidationErrors(validateDiscountAssignmentForm(values));

  const clearServerError = () => {
    dispatch(actions.physicalCards.clearPhysicalCardDiscountErrorAction());
  };

  const handleCancel = () => {
    clearServerError();
    dispatch(actions.modals.clearModals());
  };

  const handleInputChange = ({ target: { name, value } }) => {
    if (name === 'membership_id') {
      const membershipId = value === '' ? '' : Number(value);
      setValues(prev => ({
        ...prev,
        membership_id: membershipId,
        ...(membershipId
          ? {}
          : {
              discount_valid_from: '',
              discount_valid_from_time: '',
              discount_valid_to: '',
              discount_valid_to_time: '',
            }),
      }));
      setErrors(prev => {
        const next = { ...prev };
        delete next.membership_id;
        delete next.discount_valid_from;
        delete next.discount_valid_to;
        return next;
      });
      clearServerError();
      return;
    }

    if (name === 'discount_valid_to') {
      setValues(prev => ({
        ...prev,
        discount_valid_to: value,
        ...(value ? {} : { discount_valid_to_time: '' }),
      }));
      setErrors(prev => {
        const next = { ...prev };
        delete next.discount_valid_to;
        return next;
      });
      clearServerError();
      return;
    }

    setValues(prev => ({ ...prev, [name]: value }));
    setErrors(prev => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
    clearServerError();
  };

  const handleSubmit = e => {
    e.preventDefault();

    const nextErrors = validateDiscountAssignmentForm(values);
    setErrors(nextErrors);

    if (hasValidationErrors(nextErrors) || loading) {
      return;
    }

    const body = buildDiscountPayload(values);
    if (!body) return;

    dispatch(actions.physicalCards.addPhysicalCardDiscountAction(cardId, body));
  };

  const hasDiscountPlan = Boolean(values.membership_id);

  return (
    <form className={styles.discountForm} onSubmit={handleSubmit} method="POST">
      <div className={styles.field}>
        <Select
          name="membership_id"
          label="Nuolaidos planas"
          options={membershipOptions}
          selectedValue={values.membership_id}
          onChange={handleInputChange}
          labelContainerClassName={styles.selectLabel}
        />
        {displayErrors.membership_id && (
          <span className={styles.fieldError}>
            {displayErrors.membership_id}
          </span>
        )}
      </div>

      {hasDiscountPlan && (
        <>
          <div className={styles.field}>
            <div className={styles.dateTimeRow}>
              <Input
                name="discount_valid_from"
                label="Galioja nuo"
                type="date"
                value={values.discount_valid_from}
                onChange={handleInputChange}
                max="2999-12-31"
                required
              />
              <Input
                name="discount_valid_from_time"
                label="Laikas"
                type="time"
                value={values.discount_valid_from_time}
                onChange={handleInputChange}
              />
            </div>
            {displayErrors.discount_valid_from && (
              <span className={styles.fieldError}>
                {displayErrors.discount_valid_from}
              </span>
            )}
          </div>

          <div className={styles.field}>
            <div className={styles.dateTimeRow}>
              <Input
                name="discount_valid_to"
                label="Galioja iki"
                type="date"
                value={values.discount_valid_to}
                onChange={handleInputChange}
                max="2999-12-31"
              />
              <Input
                name="discount_valid_to_time"
                label="Laikas"
                type="time"
                value={values.discount_valid_to_time}
                onChange={handleInputChange}
                disabled={!values.discount_valid_to}
              />
            </div>
            {displayErrors.discount_valid_to && (
              <span className={styles.fieldError}>
                {displayErrors.discount_valid_to}
              </span>
            )}
          </div>
        </>
      )}

      {discountError && (
        <span className={styles.fieldError}>{discountError}</span>
      )}

      <div className={styles.discountFormActions}>
        <Button
          color="dim"
          type="button"
          onClick={handleCancel}
          disabled={loading}
        >
          Atšaukti
        </Button>
        <Button
          color={isSubmitDisabled ? 'outline' : 'primary'}
          type="submit"
          disabled={isSubmitDisabled}
        >
          Pridėti
        </Button>
      </div>
    </form>
  );
};

const mapStateToProps = state => ({
  discountError: state.physicalCards.discountError,
  loading: state.physicalCards.loading,
  memberships: state.memberships,
});

AddDiscount.propTypes = {
  dispatch: PropTypes.func.isRequired,
  cardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  discountError: PropTypes.string,
  loading: PropTypes.bool,
  memberships: PropTypes.shape({
    memberships: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        title_lt: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
};

AddDiscount.defaultProps = {
  discountError: '',
  loading: false,
};

export default connect(mapStateToProps)(AddDiscount);
